import { Button, Grid, Group, Stack, Text } from "@mantine/core"
import { IconCirclePlus, IconX } from "@tabler/icons-react"
import { LabelWithTooltip, Select, TextInput } from "@tfg/ui"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { registrationIdStringFromRow } from "~/utils/registration-id-string-from-row"
import { useRegistrationIdSchema } from "~/utils/schemas/useRegistrationIdSchema"

export type RegistrationIdItem = {
  id?: string
  registrationId: string
  /** Wrong-shaped rows may carry the identifier here only; prefer `registrationId`. */
  readonly value?: string
  type: string
}

export type SelectListRegistrationIdFieldOptions = Readonly<{
  label: string
  value: string
  description?: string
}>

export type SelectListRegistrationIdFieldProps = {
  readonly value: RegistrationIdItem[]
  readonly onChange: (list: RegistrationIdItem[]) => void
  readonly onValidate?: () => void
  readonly options: ReadonlyArray<SelectListRegistrationIdFieldOptions>
  readonly label: string
  readonly tooltip?: string
  readonly isRequired?: boolean
  readonly placeholder?: string
  readonly disabled?: boolean
  /** Rows matching `${type}:${registrationId}` cannot be removed (e.g. directory prefill). */
  readonly removalBlockedKeys?: ReadonlySet<string> | null
  readonly fieldError?: string
  readonly labelProps?: { fz?: string; fw?: number }
  readonly showLabelOnlyWhenNoTooltip?: boolean
  readonly classNames?: Record<string, string>
}

export function SelectListRegistrationIdField({
  value,
  onChange,
  onValidate,
  options,
  label,
  tooltip,
  isRequired = false,
  placeholder,
  disabled,
  removalBlockedKeys = null,
  fieldError,
  labelProps,
  showLabelOnlyWhenNoTooltip = false,
  classNames,
}: SelectListRegistrationIdFieldProps): JSX.Element {
  const registrationIdSchema = useRegistrationIdSchema()
  const [textInputValue, setTextInputValue] = useState("")
  const [selectInputValue, setSelectInputValue] = useState(
    options[0]?.value ?? ""
  )
  const { t } = useTranslation("supplier")
  const [localError, setLocalError] = useState<string | null>(null)

  const generalValue: { id?: string; value: string; type: string }[] =
    value.map((item) => ({
      id: item.id,
      value: registrationIdStringFromRow(item),
      type: item.type,
    }))

  const availableOptions = options.filter((opt) => {
    if (opt.label === "OTHER") return true
    return !generalValue.some((item) => item.type === opt.value)
  })

  const currentSelectValue = availableOptions.some(
    (o) => o.value === selectInputValue
  )
    ? selectInputValue
    : (availableOptions[0]?.value ?? "")

  const handleAdd = (): void => {
    const parsed = registrationIdSchema.safeParse({
      value: textInputValue,
      type: currentSelectValue,
    })
    if (!parsed.success) {
      setLocalError(parsed.error.issues[0]?.message ?? "Invalid")
      return
    }
    if (!textInputValue.trim() || !currentSelectValue) return
    const newList: RegistrationIdItem[] = [
      ...value,
      {
        registrationId: textInputValue.trim(),
        type: currentSelectValue,
      },
    ]
    onChange(newList)
    onValidate?.()
    setTextInputValue("")
    setSelectInputValue(availableOptions[0]?.value ?? "")
    setLocalError(null)
  }

  const handleRemove = (itemIndex: number): void => {
    const newList = value.filter((_, index) => index !== itemIndex)
    onChange(newList)
    onValidate?.()
  }

  const showLabelInHeader = tooltip || !showLabelOnlyWhenNoTooltip
  const showLabelOnInput = showLabelOnlyWhenNoTooltip && !tooltip

  return (
    <Stack gap="xs" w="100%">
      {showLabelInHeader && (
        <LabelWithTooltip
          label={label}
          hoverHelperText={tooltip}
          w="100%"
          justify="space-between"
          withAsterisk={isRequired}
          {...labelProps}
        />
      )}
      <Stack gap="xxs">
        <Group align="start" w="100%" gap="xs">
          <TextInput
            style={{ flex: "0 0 70%" }}
            label={showLabelOnInput ? label : undefined}
            labelProps={showLabelOnInput ? labelProps : undefined}
            placeholder={placeholder}
            value={textInputValue}
            onChange={(e) => setTextInputValue(e.currentTarget.value)}
            fz="xs"
            disabled={disabled}
            classNames={classNames}
            error={localError}
          />
          <Select
            style={{ flex: "1 1 0" }}
            data={availableOptions.map((o) => ({
              label: o.label,
              value: o.value,
            }))}
            value={currentSelectValue}
            onChange={(val) => setSelectInputValue(val ?? "")}
            disabled={disabled}
          />
          <Button
            type="button"
            variant="transparent"
            styles={{
              root: {
                minWidth: "unset",
                width: "fit-content",
                height: "100%",
                display: "flex",
                alignItems: "start",
              },
            }}
            size="sm"
            onClick={handleAdd}
            disabled={disabled}
            aria-label={t("productionSiteModal.actions.addRegistrationId", {
              defaultValue: "Add registration ID",
            })}
            px={0}
            py="xxs"
          >
            <IconCirclePlus size={20} />
          </Button>
        </Group>
        {!fieldError && generalValue.length === 0 && (
          <Text fz="xs" c="dimmed">
            {t("productionSiteModal.registrationIdDescriptions.VAT")}
          </Text>
        )}
      </Stack>
      {generalValue.length > 0 && (
        <Stack gap={0} style={{ borderLeft: "1px solid #e5e7eb" }}>
          {generalValue.map((item, index) => (
            <Grid
              align="center"
              key={
                item.id !== undefined
                  ? item.id
                  : `${item.value}-${item.type}-${index}`
              }
            >
              <Grid.Col span={7}>
                <Text pl={10}>{item.value}</Text>
              </Grid.Col>
              <Grid.Col span={3}>
                <Text pl={12}>{item.type}</Text>
              </Grid.Col>
              <Grid.Col pl={8} span={2}>
                {(() => {
                  const isRemovalBlocked =
                    disabled ||
                    (removalBlockedKeys !== null &&
                      removalBlockedKeys.has(`${item.type}:${item.value}`))
                  if (isRemovalBlocked) {
                    return null
                  }
                  return (
                    <Button
                      type="button"
                      variant="transparent"
                      color="red"
                      size="sm"
                      onClick={() => handleRemove(index)}
                      disabled={disabled}
                      aria-label={t(
                        "productionSiteModal.actions.removeRegistrationId",
                        {
                          defaultValue: "Remove registration ID",
                        }
                      )}
                    >
                      <IconX size={16} />
                    </Button>
                  )
                })()}
              </Grid.Col>
            </Grid>
          ))}
        </Stack>
      )}
      {fieldError && (
        <Text fz="xs" c="red">
          {fieldError}
        </Text>
      )}
    </Stack>
  )
}
